import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataService } from '../core/data.service';
import { SessionService } from '../core/session.service';
import { PortalFile } from './files.models';

@Injectable({ providedIn: 'root' })
export class FilesApiService {
  /** A fresh HttpClient that bypasses all interceptors. Used for the cross-origin
   * PUT to the Teams/SharePoint upload URL — the AuthInterceptor would otherwise
   * stamp `withCredentials: true` on the request, which SharePoint rejects via CORS. */
  private rawHttp: HttpClient;

  constructor(
    private dataSvc: DataService,
    private http: HttpClient,
    private session: SessionService,
    backend: HttpBackend
  ) {
    this.rawHttp = new HttpClient(backend);
  }

  getFiles(): Observable<PortalFile[]> {
    // V1 parity: FilesGet requires cid; the V2 InjectSessionContext only stamps
    // SessionID/CRID, not cid, so the front-end has to pass it explicitly.
    const cid = this.session.userInfo?.customer_id || '';
    return this.dataSvc.dgGet([{
      action: 'FilesGet',
      returnas: 'BASE',
      cid
    }]).pipe(
      map(res => {
        // Possible shapes from the bridge:
        //   [{Data: [...]}]       — V2 standard wrap
        //   [{...}, {...}]        — flat array of files
        //   {Data: [...]}         — V1 raw shape after auto-unwrap
        //   [...]                 — already an array
        let files: any = res;
        if (Array.isArray(files) && files.length === 1 && files[0]?.Data) {
          files = files[0].Data;
        } else if (!Array.isArray(files) && files?.Data) {
          files = files.Data;
        }
        return Array.isArray(files) ? files : [];
      })
    );
  }

  downloadFile(fileId: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'DownloadFile',
      returnas: 'BASE',
      fileId
    }]);
  }

  deleteFile(fileId: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'DeleteFile',
      returnas: 'BASE',
      fileId
    }]);
  }

  activateFile(fileId: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'ActivateFile',
      returnas: 'BASE',
      fileId
    }]);
  }

  requestUploadUrl(fileTypeId: string, fileTypeName: string, fileName: string,
                   extension: string, customerId: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'UploadFile',
      returnas: 'BASE',
      fileTypeId,
      fileTypeName,
      fileName,
      extension,
      customerId
    }]);
  }

  uploadChunkToTeams(uploadUrl: string, chunk: Blob, start: number, end: number, fileSize: number): Observable<any> {
    // rawHttp bypasses interceptors so the request stays anonymous (no cookies)
    // and matches V1's transformRequest:angular.identity behavior.
    return this.rawHttp.put(uploadUrl, chunk, {
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Range': `bytes ${start}-${end - 1}/${fileSize}`
      },
      reportProgress: true,
      observe: 'events'
    });
  }

  updateOrAddFile(action: string, params: any): Observable<any> {
    return this.dataSvc.dgGet([{
      action,
      returnas: 'BASE',
      ...params
    }]);
  }

  saveFileToDBAsBytes(customerId: string, fileTypeId: string, filename: string,
                      extension: string, base64: string): Observable<any> {
    return this.dataSvc.dgGet([{
      action: 'SaveFileToDBAsBytes',
      returnas: 'BASE',
      customer_id: customerId,
      filetype_id: fileTypeId,
      filename,
      extension,
      fileBytesBase64: base64
    }]);
  }
}
